
import React, { useState } from 'react';
import { Plus, CheckSquare, Filter } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useCampaignTasks, type TaskStatus } from '@/hooks/useCampaignTasks';
import { TaskCard } from './TaskCard';
import { AddTaskModal } from './AddTaskModal';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

interface CampaignTasksSectionProps {
  campaignId: string;
}

export const CampaignTasksSection: React.FC<CampaignTasksSectionProps> = ({ campaignId }) => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState<TaskStatus | 'all'>('all');

  const filters = statusFilter !== 'all' ? { status: statusFilter as TaskStatus } : undefined;
  const { data: tasks, isLoading, error } = useCampaignTasks(campaignId, filters);

  const tasksByStatus = {
    all: tasks?.length || 0,
    open: tasks?.filter(t => t.status === 'open').length || 0,
    in_progress: tasks?.filter(t => t.status === 'in_progress').length || 0,
    completed: tasks?.filter(t => t.status === 'completed').length || 0,
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Hanken_Grotesk',sans-serif] text-[20px] font-bold">
            <CheckSquare className="w-5 h-5" />
            Tasks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <LoadingSpinner size="medium" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 font-['Hanken_Grotesk',sans-serif] text-[20px] font-bold">
            <CheckSquare className="w-5 h-5" />
            Tasks
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8 text-red-600">
            Error loading tasks. Please try again.
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 font-['Hanken_Grotesk',sans-serif] text-[20px] font-bold">
              <CheckSquare className="w-5 h-5" />
              Tasks ({tasksByStatus[statusFilter]})
            </CardTitle>
            <div className="flex items-center gap-2">
              <Select value={statusFilter} onValueChange={(value: TaskStatus | 'all') => setStatusFilter(value)}>
                <SelectTrigger className="w-32">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All ({tasksByStatus.all})</SelectItem>
                  <SelectItem value="open">Open ({tasksByStatus.open})</SelectItem>
                  <SelectItem value="in_progress">In Progress ({tasksByStatus.in_progress})</SelectItem>   
                  <SelectItem value="completed">Completed ({tasksByStatus.completed})</SelectItem>
                </SelectContent>
              </Select>
              <Button
                onClick={() => setShowAddModal(true)}
                size="sm"
                className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white"
              >
                <Plus className="w-4 h-4" />
                Add Task
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {!tasks || tasks.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <CheckSquare className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No tasks yet. Create your first task to get started.</p>
            </div>
          ) : (
            tasks.map((task) => (
              <TaskCard
                key={task.id}
                task={task}
                campaignId={campaignId}
              />
            ))
          )}
        </CardContent>
      </Card>

      {showAddModal && (
        <AddTaskModal
          campaignId={campaignId}
          onClose={() => setShowAddModal(false)}
        />
      )}
    </>
  );
};
